import { useState, useEffect, useMemo, useContext } from "react"
import firebase from "firebase"
import "./quote-assistant.scss"

// Firebase
import { db } from "../../utils/firebase"

// Providers
import { AlertsContext } from "../../utils/providers/alerts"

// Icons
import { Plus } from "lucide-react"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Button from "../../components/ui/button/button"

// Page components
import FlowList from "./components/flow-list"
import FlowBuilder from "./components/flow-builder"
import FlowDiagram from "./components/flow-diagram"

// ---------------------------------------------------------------------------
// Quote Assistant - a two-pane screen for mapping out quoting flows. On the
// left, agents build a flow: pick the hotel a client enquired for, then the
// alternative hotels we'd quote alongside it. On the right, a live top-down
// diagram of the flow draws itself as the choices are made.
//
// Flows live in the quote_flows collection as:
//   { start: { id, title } | null, alternatives: [{ id, title }], created, updated }
// ---------------------------------------------------------------------------
export default function QuoteAssistant() {
	const { pushAlert } = useContext(AlertsContext)

	// All saved flows, streamed in realtime
	const [flows, setFlows] = useState([])
	const [loading, setLoading] = useState(true)

	// The flow open in the builder, and the one being preview-hovered in the list
	const [activeFlowId, setActiveFlowId] = useState(null)
	const [previewFlowId, setPreviewFlowId] = useState(null)

	// Is a new flow being written to the database?
	const [creating, setCreating] = useState(false)

	// What the agent is mid-typing into one of the pickers ({ target, text }),
	// so the diagram can sketch the node before it's actually chosen
	const [sketch, setSketch] = useState(null)

	// Stream the saved flows, newest first
	useEffect(() => {
		const unsubscribe = db
			.collection("quote_flows")
			.orderBy("created", "desc")
			.onSnapshot((snapshot) => {
				setFlows(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
				setLoading(false)
			})
		return () => unsubscribe()
	}, [])

	// The open flow. Falls back to an empty shell when the snapshot hasn't
	// caught up yet (e.g. the moment after creating a flow, while the server
	// timestamp resolves) so the builder opens instantly.
	const activeFlow = useMemo(() => {
		if (!activeFlowId) return null
		return flows.find((flow) => flow.id === activeFlowId) || { id: activeFlowId, start: null, alternatives: [] }
	}, [flows, activeFlowId])

	const previewFlow = useMemo(() => flows.find((flow) => flow.id === previewFlowId) || null, [flows, previewFlowId])

	// Create a new, empty flow and jump straight into the builder
	const createFlow = async () => {
		setCreating(true)
		try {
			const reference = await db.collection("quote_flows").add({
				start: null,
				alternatives: [],
				created: firebase.firestore.FieldValue.serverTimestamp(),
				updated: firebase.firestore.FieldValue.serverTimestamp(),
			})
			setPreviewFlowId(null)
			setActiveFlowId(reference.id)
		} catch (error) {
			pushAlert({ type: "ERROR", title: "Could not create the flow", body: error.message })
		} finally {
			setCreating(false)
		}
	}

	// Merge a partial update into the open flow's document. The realtime
	// listener (with latency compensation) feeds the change straight back into
	// the builder and diagram.
	const saveFlow = async (partial) => {
		try {
			await db.doc(`quote_flows/${activeFlowId}`).set(
				{
					...partial,
					updated: firebase.firestore.FieldValue.serverTimestamp(),
				},
				{ merge: true }
			)
		} catch (error) {
			pushAlert({ type: "ERROR", title: "Could not save the flow", body: error.message })
		}
	}

	// Delete the open flow and drop back to the list
	const deleteFlow = async () => {
		try {
			await db.doc(`quote_flows/${activeFlowId}`).delete()
			setActiveFlowId(null)
			setSketch(null)
		} catch (error) {
			pushAlert({ type: "ERROR", title: "Could not delete the flow", body: error.message })
		}
	}

	// Open a flow in the builder. The hover-preview is cleared here because the
	// list unmounts before its mouse-leave can fire.
	const openFlow = (flowId) => {
		setPreviewFlowId(null)
		setActiveFlowId(flowId)
	}

	// Close the builder back to the flow list
	const closeBuilder = () => {
		setActiveFlowId(null)
		setPreviewFlowId(null)
		setSketch(null)
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Quote Assistant</h1>

				{!activeFlow && (
					<Button
						small={true}
						label="New flow"
						icon={<Plus size={15} />}
						loading={creating}
						loadingText="Creating..."
						onClick={createFlow}
					/>
				)}
			</Title>

			<div className="quote-assistant">
				{/* Left pane - the flow list, or the builder when a flow is open */}
				<div className="qa-panel">
					{activeFlow ? (
						<FlowBuilder
							flow={activeFlow}
							onBack={closeBuilder}
							onSave={saveFlow}
							onDelete={deleteFlow}
							onSketch={setSketch}
						/>
					) : (
						<FlowList
							flows={flows}
							loading={loading}
							creating={creating}
							onOpen={openFlow}
							onCreate={createFlow}
							onPreview={setPreviewFlowId}
						/>
					)}
				</div>

				{/* Right pane - the live flow diagram. Draws the open flow, or the
				    one being hover-previewed in the list */}
				<div className="qa-canvas">
					<FlowDiagram
						flow={activeFlow || previewFlow}
						sketch={activeFlow ? sketch : null}
						isPreview={!activeFlow && !!previewFlow}
					/>
				</div>
			</div>
		</Tile>
	)
}
